import { useIntersectionObserver, useSyncedRef, useThrottledCallback } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";

export interface UseTrackImpressionEventByIntersectionParameters<T> {
  ga4TrackingHandler?: ((event: T) => void) | null;
  getGa4Event?: (() => T) | null;
  options?: TrackImpressionEventByIntersectionOptions;
}

interface TrackImpressionEventByIntersectionOptions {
  intersectionThreshold?: number[];
  isSendGA4Tracking?: boolean;
  shouldDisable?: boolean;
  shouldSendOnce?: boolean;
  shouldThrottle?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useTrackImpressionEventByIntersection = <T extends unknown>(
  parameters: UseTrackImpressionEventByIntersectionParameters<T>,
) => {
  const { ga4TrackingHandler, getGa4Event, options } = parameters;
  const latestGetGa4Event = useSyncedRef(getGa4Event);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [hasSent, setHasSent] = useState(false);
  const [intersectionObserverEntry, setIntersectionObserverEntry] = useState<
    IntersectionObserverEntry | undefined
  >(undefined);

  const getShouldSend = (): boolean => {
    if (options?.shouldDisable) {
      return false;
    }
    if (options?.shouldSendOnce) {
      return !hasSent;
    }
    return true;
  };

  const shouldSend = getShouldSend();
  const intersection = useIntersectionObserver(
    shouldSend && latestGetGa4Event.current ? targetElement : null,
    {
      threshold: options?.intersectionThreshold,
    },
  );
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIntersectionObserverEntry(intersection);
  }, [intersection]);

  const handleSend = useThrottledCallback(
    (ga4Event: null | T) => {
      if (options?.isSendGA4Tracking && ga4Event && ga4TrackingHandler) {
        ga4TrackingHandler(ga4Event);
      }
      setHasSent(true);
    },
    [ga4TrackingHandler, setHasSent],
    options?.shouldThrottle ? 300 : 0,
    true,
  );

  useEffect(() => {
    if (!shouldSend || !intersectionObserverEntry?.isIntersecting || !latestGetGa4Event.current)
      return;
    handleSend(latestGetGa4Event.current ? latestGetGa4Event.current() : null);
  }, [shouldSend, intersectionObserverEntry?.isIntersecting, handleSend, latestGetGa4Event]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIntersectionObserverEntry(undefined);
    setHasSent(false);
  }, [targetElement]);

  const captureTrackImpressionEventTargetElement = useCallback(
    (element: HTMLElement | null) => {
      setTargetElement(element);
    },
    [setTargetElement],
  );
  return { captureTrackImpressionEventTargetElement, intersection: intersectionObserverEntry };
};
